import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import type { LogEntry } from "@/components/catalog/MiniLogPreview";
import { showToast } from "@/components/ui";
import {
  productTypeSyncSteps as defaultSteps,
  syncScopeOptions,
  syncTargets,
} from "@/data/productTypeMockData";
import {
  useAmazonProductTypeSchemaDigestQuery,
  useAmazonSearchProductTypesQuery,
  useCoupangCategoryRecommendQuery,
  useCoupangCategorySchemaDigestQuery,
  useEbayCategorySchemaDigestQuery,
  useEbayCategorySearchQuery,
  useFlipkartVerticalSchemaDigestQuery,
  useFlipkartVerticalSearchQuery,
  useMeeshoProductTypeSchemaDigestQuery,
  useMeeshoSearchProductTypesQuery,
  useRefreshFlipkartVerticalsMutation,
  useShopifyProductTypeSchemaDigestQuery,
  useShopifySearchProductTypesQuery,
  useSnapdealCategorySchemaDigestQuery,
  useSnapdealCategorySearchQuery,
  useStartProductTypeSyncMutation,
  useViewerBootstrapQuery,
} from "@/graphql/generated";
import { getMarketplaceSyncStrategy } from "@/lib/productTypeSync/marketplaceSyncStrategy";
import {
  deriveSyncFailureMessage,
  isFailureSyncStatus,
} from "@/lib/productTypeSync/syncFailure";
import { subscriptionUpdateToLogEntry } from "@/lib/productTypeSync/subscriptionUpdateToLogEntry";
import type {
  ProductTypeItem,
  SyncStepState,
  UseProductTypeSyncOptions,
} from "./useProductTypeSync.types";
import { useSessionKeepAlive } from "./useSessionKeepAlive";
import { useTaxonomyIngestionSessionSubscription } from "./useTaxonomyIngestionSessionSubscription";

const SUCCESS_STATUSES = ["COMPLETED", "DONE", "REVIEW_READY"];
const TERMINAL_STATUSES = [...SUCCESS_STATUSES, "FAILED", "ERROR", "CANCELLED"];
const STAGE_STEP_INDEX: Record<string, number> = {
  QUEUED: 0,
  FETCHING_SCHEMA: 0,
  PREPARING_INPUT: 1,
  MATCHING_ATTRIBUTES: 3,
  REVIEW_READY: 4,
  COMPLETED: 4,
};
// Default Amazon India marketplace ID; used when spApiMarketplaceId is not provided
const AMAZON_IN_MARKETPLACE_ID = "A21TJRUUN4KGV";

function getShopifyProductTypeDisplayName(value: string): string {
  const segments = value
    .replace(/--+/g, ">")
    .split(">")
    .map((segment) => segment.trim())
    .filter(Boolean);
  return segments.at(-1) ?? value.trim();
}

function createLocalSyncSessionId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `sync-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function useProductTypeSync(options: UseProductTypeSyncOptions = {}) {
  const {
    onSelectedProductTypeChange,
    sessionId: sessionIdProp,
    onSyncLogsChange,
    marketplaceCode = "AMAZON",
    spApiMarketplaceId,
    initialProductType,
  } = options;

  // Resolve the sync strategy for the selected marketplace
  const strategy = getMarketplaceSyncStrategy(marketplaceCode);
  // spApiMarketplaceId comes from syncConfig on the Marketplace response — no env var needed.
  const resolvedSpApiMarketplaceId =
    spApiMarketplaceId ??
    (strategy.provider === "amazon" ? AMAZON_IN_MARKETPLACE_ID : "");

  // Meesho and Coupang need the workspace context to look up per-seller
  // credentials server-side.
  const {
    data: viewerData,
    isLoading: viewerLoading,
    error: viewerError,
  } = useViewerBootstrapQuery(undefined, {
    enabled:
      strategy.provider === "meesho" ||
      strategy.provider === "coupang" ||
      strategy.provider === "snapdeal" ||
      strategy.provider === "flipkart",
  });
  const meeshoWorkspaceId = viewerData?.viewerBootstrap?.workspace?.id ?? "";
  const coupangWorkspaceId = viewerData?.viewerBootstrap?.workspace?.id ?? "";
  const snapdealWorkspaceId = viewerData?.viewerBootstrap?.workspace?.id ?? "";
  const flipkartWorkspaceId = viewerData?.viewerBootstrap?.workspace?.id ?? "";

  const [query, setQuery] = useState("");
  const [searched, setSearched] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<ProductTypeItem | null>(
    null,
  );
  const [digestExpanded, setDigestExpanded] = useState(false);
  const [scopeChecks] = useState(() =>
    Object.fromEntries(syncScopeOptions.map((o) => [o.id, o.defaultChecked])),
  );
  const [syncRunning, setSyncRunning] = useState(false);
  const [syncDone, setSyncDone] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [steps, setSteps] = useState<SyncStepState[]>(() =>
    defaultSteps.map((s) => ({
      id: s.id,
      name: s.name,
      status: "pending" as const,
    })),
  );
  const [targetStatuses, setTargetStatuses] = useState<Record<string, string>>(
    {},
  );
  const [syncSessionId, setSyncSessionId] = useState<string | null>(null);
  const [subscriptionLogs, setSubscriptionLogs] = useState<LogEntry[]>([]);
  const [startSyncPending, setStartSyncPending] = useState(false);
  const [syncConnectionWarning, setSyncConnectionWarning] = useState<
    string | null
  >(null);
  const logIdRef = useRef(0);
  const seenLogKeysRef = useRef(new Set<string>());
  const lastStatusRef = useRef<string | null>(null);
  const lastProgressRef = useRef<number | null>(null);
  const startedSessionIdRef = useRef<string | null>(null);
  const subscriptionWarningShownRef = useRef(false);
  const failureToastShownRef = useRef(false);

  const sessionId = sessionIdProp ?? syncSessionId;

  useSessionKeepAlive({
    enabled: startSyncPending || syncRunning,
    refreshOnMount: true,
  });

  // ── Amazon queries ──────────────────────────────────────────────────────────
  const {
    data: amazonSearchData,
    isFetching: amazonSearching,
    error: amazonSearchError,
  } = useAmazonSearchProductTypesQuery(
    {
      input: {
        keyword: searchKeyword ?? "",
        marketplaceId: resolvedSpApiMarketplaceId,
      },
    },
    {
      enabled:
        strategy.provider === "amazon" &&
        !!searchKeyword &&
        searchKeyword.length > 0,
    },
  );

  const {
    data: amazonDigestData,
    isLoading: amazonDigestLoading,
    error: amazonDigestError,
  } = useAmazonProductTypeSchemaDigestQuery(
    {
      input: {
        productType: selectedType?.name ?? "",
        marketplaceId: resolvedSpApiMarketplaceId,
      },
    },
    {
      enabled:
        strategy.provider === "amazon" &&
        !!selectedType?.name &&
        digestExpanded,
    },
  );

  // ── Shopify queries ─────────────────────────────────────────────────────────
  const {
    data: shopifySearchData,
    isFetching: shopifySearching,
    error: shopifySearchError,
  } = useShopifySearchProductTypesQuery(
    {
      input: {
        keyword: searchKeyword ?? "",
      },
    },
    {
      enabled:
        strategy.provider === "shopify" &&
        !!searchKeyword &&
        searchKeyword.length > 0,
    },
  );

  const {
    data: shopifyDigestData,
    isLoading: shopifyDigestLoading,
    error: shopifyDigestError,
  } = useShopifyProductTypeSchemaDigestQuery(
    {
      input: {
        productType: selectedType?.name ?? "",
      },
    },
    {
      enabled:
        strategy.provider === "shopify" &&
        !!selectedType?.name &&
        digestExpanded,
    },
  );

  // ── Meesho queries ──────────────────────────────────────────────────────────
  const {
    data: meeshoSearchData,
    isFetching: meeshoSearching,
    error: meeshoSearchError,
  } = useMeeshoSearchProductTypesQuery(
    {
      input: {
        keyword: searchKeyword ?? "",
        workspaceId: meeshoWorkspaceId,
      },
    },
    {
      enabled:
        strategy.provider === "meesho" &&
        !!searchKeyword &&
        searchKeyword.length > 0 &&
        !!meeshoWorkspaceId,
    },
  );

  const {
    data: meeshoDigestData,
    isLoading: meeshoDigestLoading,
    error: meeshoDigestError,
  } = useMeeshoProductTypeSchemaDigestQuery(
    {
      input: {
        productType: selectedType?.name ?? "",
        workspaceId: meeshoWorkspaceId,
      },
    },
    {
      enabled:
        strategy.provider === "meesho" &&
        !!selectedType?.name &&
        !!meeshoWorkspaceId &&
        digestExpanded,
    },
  );

  // ── Coupang queries ─────────────────────────────────────────────────────────
  // Coupang differs from Amazon/Shopify: instead of keyword-based search,
  // it returns a single AI-predicted category for the given productName.
  const {
    data: coupangRecommendData,
    isFetching: coupangSearching,
    error: coupangSearchError,
  } = useCoupangCategoryRecommendQuery(
    {
      input: {
        workspaceId: coupangWorkspaceId,
        productName: searchKeyword ?? "",
      },
    },
    {
      enabled:
        strategy.provider === "coupang" &&
        !!coupangWorkspaceId &&
        !!searchKeyword &&
        searchKeyword.length > 0,
    },
  );

  const {
    data: coupangDigestData,
    isLoading: coupangDigestLoading,
    error: coupangDigestError,
  } = useCoupangCategorySchemaDigestQuery(
    {
      input: {
        workspaceId: coupangWorkspaceId,
        displayCategoryCode: selectedType?.name ?? "",
      },
    },
    {
      enabled:
        strategy.provider === "coupang" &&
        !!coupangWorkspaceId &&
        !!selectedType?.name &&
        digestExpanded,
    },
  );

  // ── eBay queries ────────────────────────────────────────────────────────────
  // App token, no workspace. Search returns a categoryTreeId the digest needs.
  const {
    data: ebaySearchData,
    isFetching: ebaySearching,
    error: ebaySearchError,
  } = useEbayCategorySearchQuery(
    {
      input: {
        query: searchKeyword ?? "",
      },
    },
    {
      enabled:
        strategy.provider === "ebay" &&
        !!searchKeyword &&
        searchKeyword.length > 0,
    },
  );

  const ebayCategoryTreeId =
    ebaySearchData?.ebayCategorySearch?.categoryTreeId ?? null;

  const {
    data: ebayDigestData,
    isLoading: ebayDigestLoading,
    error: ebayDigestError,
  } = useEbayCategorySchemaDigestQuery(
    {
      input: {
        categoryTreeId: ebayCategoryTreeId ?? "",
        categoryId: selectedType?.name ?? "",
      },
    },
    {
      enabled:
        strategy.provider === "ebay" &&
        !!ebayCategoryTreeId &&
        !!selectedType?.name &&
        digestExpanded,
    },
  );

  // ── Snapdeal queries ────────────────────────────────────────────────────────
  // Keyword category search (like eBay) but workspace-scoped (like Coupang):
  // Snapdeal's category API needs the connected seller's token.
  const {
    data: snapdealSearchData,
    isFetching: snapdealSearching,
    error: snapdealSearchError,
  } = useSnapdealCategorySearchQuery(
    {
      input: {
        workspaceId: snapdealWorkspaceId,
        query: searchKeyword ?? "",
      },
    },
    {
      enabled:
        strategy.provider === "snapdeal" &&
        !!snapdealWorkspaceId &&
        !!searchKeyword &&
        searchKeyword.length > 0,
    },
  );

  const {
    data: snapdealDigestData,
    isLoading: snapdealDigestLoading,
    error: snapdealDigestError,
  } = useSnapdealCategorySchemaDigestQuery(
    {
      input: {
        workspaceId: snapdealWorkspaceId,
        categoryId: selectedType?.name ?? "",
      },
    },
    {
      enabled:
        strategy.provider === "snapdeal" &&
        !!snapdealWorkspaceId &&
        !!selectedType?.name &&
        digestExpanded,
    },
  );

  // ── Flipkart queries ────────────────────────────────────────────────────────
  // Search is DB-only (the imported vertical directory), so no workspace/token.
  // The schema digest hits the live Flipkart API and needs the connected seller.
  const {
    data: flipkartSearchData,
    isFetching: flipkartSearching,
    error: flipkartSearchError,
  } = useFlipkartVerticalSearchQuery(
    {
      input: {
        query: searchKeyword ?? "",
      },
    },
    {
      enabled:
        strategy.provider === "flipkart" &&
        !!searchKeyword &&
        searchKeyword.length > 0,
    },
  );

  const {
    data: flipkartDigestData,
    isLoading: flipkartDigestLoading,
    error: flipkartDigestError,
  } = useFlipkartVerticalSchemaDigestQuery(
    {
      input: {
        workspaceId: flipkartWorkspaceId,
        vertical: selectedType?.name ?? "",
      },
    },
    {
      enabled:
        strategy.provider === "flipkart" &&
        !!flipkartWorkspaceId &&
        !!selectedType?.name &&
        digestExpanded,
    },
  );

  // ── Merge results based on active provider ──────────────────────────────────
  // Per-provider merge; Coupang/eBay digests adapted to the UI's { aiResponse, canonicalSchemaPayload } shape.
  const byProvider = {
    shopify: {
      searching: shopifySearching,
      searchError: shopifySearchError,
      digestLoading: shopifyDigestLoading,
      digestError: shopifyDigestError,
      results: (
        shopifySearchData?.shopifySearchProductTypes?.productTypes ?? []
      ).map((t) => ({
        name: t.name,
        displayName: getShopifyProductTypeDisplayName(t.name),
        synced: t.synced,
      })),
      schemaDigest: shopifyDigestData?.shopifyProductTypeSchemaDigest ?? null,
    },
    meesho: {
      searching: meeshoSearching || viewerLoading,
      searchError: meeshoSearchError || viewerError,
      digestLoading: meeshoDigestLoading || viewerLoading,
      digestError: meeshoDigestError || viewerError,
      results: meeshoSearchData?.meeshoSearchProductTypes?.productTypes ?? [],
      schemaDigest: meeshoDigestData?.meeshoProductTypeSchemaDigest ?? null,
    },
    coupang: {
      searching: coupangSearching || viewerLoading,
      searchError: coupangSearchError || viewerError,
      digestLoading: coupangDigestLoading || viewerLoading,
      digestError: coupangDigestError || viewerError,
      results: (
        coupangRecommendData?.coupangCategoryRecommend?.categories ?? []
      ).map((c) => ({
        name: c.categoryId,
        displayName: c.categoryName,
        synced: false,
      })),
      schemaDigest: coupangDigestData?.coupangCategorySchemaDigest
        ? {
            aiResponse: null,
            canonicalSchemaPayload:
              coupangDigestData.coupangCategorySchemaDigest
                .canonicalSchemaPayload,
          }
        : null,
    },
    ebay: {
      searching: ebaySearching,
      searchError: ebaySearchError,
      digestLoading: ebayDigestLoading,
      digestError: ebayDigestError,
      // eBay category id is the productType sent to sync; show the full path.
      results: (ebaySearchData?.ebayCategorySearch?.categories ?? []).map(
        (c) => ({
          name: c.categoryId,
          displayName: c.fullPath || c.categoryName,
          synced: false,
        }),
      ),
      schemaDigest: ebayDigestData?.ebayCategorySchemaDigest
        ? {
            aiResponse: null,
            canonicalSchemaPayload:
              ebayDigestData.ebayCategorySchemaDigest.canonicalSchemaPayload,
          }
        : null,
    },
    snapdeal: {
      searching: snapdealSearching || viewerLoading,
      searchError: snapdealSearchError || viewerError,
      digestLoading: snapdealDigestLoading || viewerLoading,
      digestError: snapdealDigestError || viewerError,
      // Snapdeal category id is the productType sent to sync; show the full path.
      results: (
        snapdealSearchData?.snapdealCategorySearch?.categories ?? []
      ).map((c) => ({
        name: c.categoryId,
        displayName: c.fullPath || c.categoryName,
        synced: false,
      })),
      schemaDigest: snapdealDigestData?.snapdealCategorySchemaDigest
        ? {
            aiResponse: null,
            canonicalSchemaPayload:
              snapdealDigestData.snapdealCategorySchemaDigest
                .canonicalSchemaPayload,
          }
        : null,
    },
    flipkart: {
      searching: flipkartSearching,
      searchError: flipkartSearchError,
      digestLoading: flipkartDigestLoading || viewerLoading,
      digestError: flipkartDigestError || viewerError,
      // Flipkart vertical is the productType sent to sync; show the full path.
      results: (
        flipkartSearchData?.flipkartVerticalSearch?.verticals ?? []
      ).map((v) => ({
        name: v.vertical,
        displayName: v.fullPath || v.displayName,
        synced: false,
      })),
      schemaDigest: flipkartDigestData?.flipkartVerticalSchemaDigest
        ? {
            aiResponse: null,
            canonicalSchemaPayload:
              flipkartDigestData.flipkartVerticalSchemaDigest
                .canonicalSchemaPayload,
          }
        : null,
    },
    amazon: {
      searching: amazonSearching,
      searchError: amazonSearchError,
      digestLoading: amazonDigestLoading,
      digestError: amazonDigestError,
      results: amazonSearchData?.amazonSearchProductTypes?.productTypes ?? [],
      schemaDigest: amazonDigestData?.amazonProductTypeSchemaDigest ?? null,
    },
  };

  // Unlisted providers (e.g. "unsupported") fall back to Amazon.
  const activeMerge =
    byProvider[strategy.provider as keyof typeof byProvider] ??
    byProvider.amazon;
  const { searching, searchError, digestLoading, digestError, schemaDigest } =
    activeMerge;
  const results: ProductTypeItem[] = activeMerge.results;

  // ── startProductTypeSync mutation ───────────────────────────────────────────
  const { mutateAsync: startSyncMutation } = useStartProductTypeSyncMutation();

  // ── Flipkart directory refresh ──────────────────────────────────────────────
  // Flipkart search reads an imported vertical directory; this re-imports it
  // from Flipkart (needs the connected seller) so users don't run a script.
  const queryClient = useQueryClient();
  const {
    mutateAsync: refreshFlipkartMutation,
    isPending: refreshingDirectory,
  } = useRefreshFlipkartVerticalsMutation();

  const refreshDirectory = useCallback(async () => {
    if (!flipkartWorkspaceId) return;
    try {
      const res = await refreshFlipkartMutation({
        input: { workspaceId: flipkartWorkspaceId },
      });
      const count = res.refreshFlipkartVerticals.count;
      showToast.success(
        "Flipkart categories refreshed",
        `${count} categories imported. Search to see them.`,
      );
      await queryClient.invalidateQueries({
        queryKey: ["FlipkartVerticalSearch"],
      });
    } catch (err) {
      showToast.error(
        "Refresh failed",
        err instanceof Error
          ? err.message
          : "Could not refresh Flipkart categories.",
      );
    }
  }, [flipkartWorkspaceId, refreshFlipkartMutation, queryClient]);

  const handleSubscriptionUpdate = useCallback(
    (update: {
      status?: string | null;
      progress?: number | null;
      payload?: unknown;
    }) => {
      const payload = update.payload as Record<string, unknown> | undefined;
      const status = update.status?.toUpperCase() ?? null;
      const isFailureEvent = isFailureSyncStatus(status);
      const rawLogs = (payload?.log_entries ?? payload?.logs) as
        | Array<Record<string, unknown>>
        | undefined;

      if (rawLogs && rawLogs.length > 0) {
        const newEntries: LogEntry[] = [];
        for (const raw of rawLogs) {
          const key = String(
            raw.id ??
              raw.log_id ??
              `${raw.timestamp ?? ""}-${raw.message ?? ""}`,
          );
          if (!seenLogKeysRef.current.has(key)) {
            seenLogKeysRef.current.add(key);
            const rawLevel = String(raw.level ?? "info").toLowerCase();
            const level: LogEntry["level"] =
              rawLevel === "error" || rawLevel === "critical"
                ? "error"
                : rawLevel === "warning" || rawLevel === "warn"
                  ? "warning"
                  : rawLevel === "success"
                    ? "success"
                    : "info";
            logIdRef.current += 1;
            newEntries.push({
              id: logIdRef.current,
              timestamp: String(raw.timestamp ?? new Date().toISOString()),
              level,
              message: String(raw.message ?? ""),
            });
          }
        }
        if (newEntries.length > 0) {
          setSubscriptionLogs((prev) => [...prev, ...newEntries]);
        }
      } else {
        const statusChanged = update.status !== lastStatusRef.current;
        const progressChanged =
          update.progress != null &&
          update.progress !== lastProgressRef.current;
        if (statusChanged || progressChanged) {
          lastStatusRef.current = update.status ?? null;
          lastProgressRef.current = update.progress ?? null;
          logIdRef.current += 1;
          setSubscriptionLogs((prev) => [
            ...prev,
            subscriptionUpdateToLogEntry(update, logIdRef.current),
          ]);
        }
      }

      if (
        update.status &&
        TERMINAL_STATUSES.includes(update.status.toUpperCase())
      ) {
        setSyncRunning(false);
        setStartSyncPending(false);
        setSyncConnectionWarning(null);
        setSyncDone(SUCCESS_STATUSES.includes(status ?? ""));
        if (isFailureEvent) {
          const message = deriveSyncFailureMessage({
            status,
            payload,
          });
          setSyncError(message);
          if (!failureToastShownRef.current) {
            failureToastShownRef.current = true;
            showToast.error("Marketplace sync failed", message);
          }
        } else {
          setSyncError(null);
        }
        if (status && SUCCESS_STATUSES.includes(status)) {
          showToast.success(
            status === "REVIEW_READY"
              ? "Attribute review is ready"
              : "Product type sync finished",
            selectedType
              ? `${selectedType.displayName ?? selectedType.name} is ready in Attribute Mapping.`
              : "Review is ready in Attribute Mapping.",
          );
        }
      }

      const stageKey =
        typeof payload?.stageKey === "string"
          ? payload.stageKey.toUpperCase()
          : null;
      if (stageKey && STAGE_STEP_INDEX[stageKey] != null) {
        const activeStepIndex = STAGE_STEP_INDEX[stageKey];
        const successTerminal = status
          ? SUCCESS_STATUSES.includes(status)
          : false;
        setSteps((prev) =>
          prev.map((step, index) => {
            if (successTerminal)
              return { ...step, status: "completed" as const };
            if (index < activeStepIndex) {
              return { ...step, status: "completed" as const };
            }
            if (index === activeStepIndex) {
              return { ...step, status: "in_progress" as const };
            }
            return { ...step, status: "pending" as const };
          }),
        );
      } else if (typeof update.progress === "number") {
        const stepCount = defaultSteps.length;
        const completedSteps =
          update.progress >= 100
            ? stepCount
            : Math.floor((update.progress / 100) * stepCount);
        setSteps((prev) =>
          prev.map((s, idx) => {
            if (idx < completedSteps)
              return { ...s, status: "completed" as const };
            if (idx === completedSteps && completedSteps < stepCount)
              return { ...s, status: "in_progress" as const };
            return s;
          }),
        );
      }

      if (payload?.steps && Array.isArray(payload.steps)) {
        setSteps((prev) =>
          prev.map((s, i) => {
            const step = (payload.steps as Record<string, unknown>[])[i];
            const status = step?.status as string | undefined;
            if (status === "completed")
              return { ...s, status: "completed" as const };
            if (status === "in_progress" || status === "running")
              return { ...s, status: "in_progress" as const };
            return s;
          }),
        );
      }
      if (
        payload?.targetStatuses &&
        typeof payload.targetStatuses === "object"
      ) {
        setTargetStatuses(payload.targetStatuses as Record<string, string>);
      }

      if (isFailureEvent) {
        setSteps((prev) => {
          const inProgressIndex = prev.findIndex(
            (step) => step.status === "in_progress",
          );
          const completedCount = prev.filter(
            (step) => step.status === "completed",
          ).length;
          const failedIndex =
            inProgressIndex >= 0
              ? inProgressIndex
              : Math.min(completedCount, Math.max(prev.length - 1, 0));

          return prev.map((step, index) =>
            index === failedIndex
              ? { ...step, status: "failed" as const }
              : step,
          );
        });
      }
    },
    [selectedType],
  );

  useTaxonomyIngestionSessionSubscription({
    runId: sessionId ?? null,
    onEvent: (event) => {
      setSyncConnectionWarning(null);
      const payload =
        event.payload && typeof event.payload === "object"
          ? (event.payload as Record<string, unknown>)
          : {};
      handleSubscriptionUpdate({
        status: event.status,
        progress: event.progressPercent,
        payload: {
          ...payload,
          stageKey: event.stageKey,
          stageLabel: event.stageLabel,
          message: event.userMessage,
          mappingSessionId: event.mappingSessionId,
          externalSessionId: event.externalSessionId,
          technicalMessage: event.technicalMessage,
          approxRemainingLabel: event.approxRemainingLabel,
          error: isFailureSyncStatus(event.status)
            ? (event.technicalMessage ?? event.userMessage)
            : null,
        },
      });
    },
    onError: () => {
      setStartSyncPending(false);
      const message =
        "Live sync updates disconnected. The sync may still be running; refresh the page to reconnect.";
      setSyncConnectionWarning(message);
      if (!subscriptionWarningShownRef.current) {
        subscriptionWarningShownRef.current = true;
        showToast.warning("Live updates interrupted", message);
      }
    },
    enabled: !!sessionId,
  });

  const handleSearch = useCallback(() => {
    const trimmed = query.trim();
    if (!trimmed) return;
    setSelectedType(null);
    onSelectedProductTypeChange?.(null);
    setSearchKeyword(trimmed);
    setSearched(true);
  }, [query, onSelectedProductTypeChange]);

  const handleSelect = useCallback(
    (pt: ProductTypeItem) => {
      setSelectedType(pt);
      onSelectedProductTypeChange?.(pt);
      setSyncRunning(false);
      setSyncDone(false);
      setStartSyncPending(false);
      setSteps(
        defaultSteps.map((s) => ({
          id: s.id,
          name: s.name,
          status: "pending" as const,
        })),
      );
      setTargetStatuses({});
      setDigestExpanded(false);
      setSubscriptionLogs([]);
      setSyncError(null);
      setSyncConnectionWarning(null);
      setSyncSessionId(null);
      startedSessionIdRef.current = null;
      seenLogKeysRef.current.clear();
      subscriptionWarningShownRef.current = false;
      failureToastShownRef.current = false;
    },
    [onSelectedProductTypeChange],
  );

  const initialProductTypeName = initialProductType?.name ?? null;
  const initialProductTypeDisplayName = initialProductType?.displayName ?? null;
  const initialProductTypeSynced = initialProductType?.synced ?? null;

  useEffect(() => {
    if (!initialProductTypeName) return;
    const nextProductType: ProductTypeItem = {
      name: initialProductTypeName,
      displayName: initialProductTypeDisplayName,
      synced: initialProductTypeSynced,
    };
    setQuery(initialProductTypeDisplayName || initialProductTypeName);
    setSearchKeyword(initialProductTypeName);
    setSearched(true);
    handleSelect(nextProductType);
  }, [
    handleSelect,
    initialProductTypeDisplayName,
    initialProductTypeName,
    initialProductTypeSynced,
  ]);

  const activeTargets = syncTargets;

  const doStartSync = useCallback((sid: string) => {
    setStartSyncPending(false);
    setSyncRunning(true);
    setSyncDone(false);
    setSyncError(null);
    setSyncConnectionWarning(null);
    setSubscriptionLogs([]);
    setSyncSessionId(sid);
    startedSessionIdRef.current = sid;
    subscriptionWarningShownRef.current = false;
    setDigestExpanded(true);
    setSteps(
      defaultSteps.map((s) => ({
        id: s.id,
        name: s.name,
        status: "pending" as const,
      })),
    );
    setTargetStatuses({});
    seenLogKeysRef.current.clear();
    lastStatusRef.current = null;
    lastProgressRef.current = null;
    failureToastShownRef.current = false;
  }, []);

  const handleReset = useCallback(() => {
    setQuery("");
    setSearched(false);
    setSearchKeyword(null);
    setSelectedType(null);
    onSelectedProductTypeChange?.(null);
    setDigestExpanded(false);
    setSyncRunning(false);
    setSyncDone(false);
    setSyncError(null);
    setSyncConnectionWarning(null);
    setStartSyncPending(false);
    setSteps(
      defaultSteps.map((s) => ({
        id: s.id,
        name: s.name,
        status: "pending" as const,
      })),
    );
    setTargetStatuses({});
    setSubscriptionLogs([]);
    setSyncSessionId(null);
    startedSessionIdRef.current = null;
    seenLogKeysRef.current.clear();
    subscriptionWarningShownRef.current = false;
    lastStatusRef.current = null;
    lastProgressRef.current = null;
    failureToastShownRef.current = false;
  }, [onSelectedProductTypeChange]);

  const requestSyncStart = useCallback(async () => {
    if (!selectedType) return;

    setStartSyncPending(true);
    setSyncRunning(false);
    setSyncDone(false);
    setSyncError(null);
    setSyncConnectionWarning(null);
    failureToastShownRef.current = false;

    try {
      const clientRequestId = createLocalSyncSessionId();
      const result = await startSyncMutation({
        marketplace: strategy.syncMarketplace,
        productType: selectedType.name,
        sessionId: clientRequestId,
      });
      const sid = result.startProductTypeSync.sessionId;
      doStartSync(sid);
    } catch (err) {
      setStartSyncPending(false);
      setSyncRunning(false);
      setSyncDone(false);
      setSyncError(null);
      setSyncConnectionWarning(null);
      setSyncSessionId(null);
      startedSessionIdRef.current = null;
      showToast.error(
        "Backend sync ack failed",
        err instanceof Error
          ? err.message
          : "The backend did not confirm the sync start request.",
      );
    }
  }, [doStartSync, selectedType, startSyncMutation, strategy]);

  const triggerSync = useCallback(() => {
    if (!selectedType) return;
    void requestSyncStart();
  }, [requestSyncStart, selectedType]);

  const handleStartSync = useCallback(() => {
    if (!selectedType) return;
    void requestSyncStart();
  }, [requestSyncStart, selectedType]);

  const handleRetry = useCallback(() => {
    const failedTargets = Object.entries(targetStatuses)
      .filter(([, s]) => s === "FAILED")
      .map(([id]) => id);
    if (failedTargets.length === 0) return;
    const updated = { ...targetStatuses };
    failedTargets.forEach((id) => {
      updated[id] = "RUNNING";
    });
    setTargetStatuses(updated);
    setTimeout(() => {
      failedTargets.forEach((id) => {
        updated[id] = "COMPLETED";
      });
      setTargetStatuses({ ...updated });
      showToast.success(
        "Retry successful",
        "Failed targets synced successfully.",
      );
    }, 2000);
  }, [targetStatuses]);

  const hasFailedTargets = Object.values(targetStatuses).includes("FAILED");

  useEffect(() => {
    onSyncLogsChange?.(subscriptionLogs);
  }, [subscriptionLogs, onSyncLogsChange]);

  return {
    query,
    setQuery,
    setSearched,
    searched,
    searchKeyword,
    handleSearch,
    results,
    searching,
    searchError: searchError ? (searchError as Error).message : null,
    selectedType,
    handleSelect,
    schemaDigest,
    digestLoading,
    digestError: digestError ? (digestError as Error).message : null,
    digestExpanded,
    setDigestExpanded,
    activeTargets,
    syncRunning,
    syncDone,
    syncError,
    syncConnectionWarning,
    startSyncPending,
    steps,
    targetStatuses,
    triggerSync,
    handleStartSync,
    handleReset,
    handleRetry,
    hasFailedTargets,
    logs: subscriptionLogs,
    scopeChecks,
    /** The resolved strategy for the current marketplace. */
    strategy,
    /** Re-import the Flipkart vertical directory (Flipkart only). */
    refreshDirectory,
    refreshingDirectory,
    /** False while the store is still resolving, so the refresh button can't no-op. */
    canRefreshDirectory: !!flipkartWorkspaceId,
    /** Resolved marketplace code (e.g. "AMAZON", "SHOPIFY"). */
    marketplaceCode,
  };
}
